import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { ArrowLeft, ExternalLink, Share2, Check } from 'lucide-react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to get full image URL from relative or absolute path
const getImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  return `${BACKEND_URL}${url}`;
};

export const ApartmentDetailPage = () => {
  const { id } = useParams();
  const { t, convertPrice, language } = useApp();
  const [apartment, setApartment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImage, setCurrentImage] = useState(0);
  const [colorSettings, setColorSettings] = useState({
    status_available: '#22c55e',
    status_occupied: '#ef4444',
    status_available_soon: '#f59e0b',
    label_promo: '#facc15',
    label_recommended: '#3b82f6',
    label_new: '#10b981'
  });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    captcha_answer: '',
  });

  useEffect(() => {
    fetchApartment();
    fetchColorSettings();
  }, [id]);

  const fetchColorSettings = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/settings/colors`);
      setColorSettings(data);
    } catch (error) {
      console.log('Using default colors');
    }
  };

  const fetchApartment = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${BACKEND_URL}/api/apartments/${id}`);
      setApartment(data);
    } catch (error) {
      toast.error('Apartment not found');
    } finally {
      setLoading(false);
    }
  };

  // Helper to determine text color based on background brightness
  const getContrastColor = (hexColor) => {
    const r = parseInt(hexColor.slice(1, 3), 16);
    const g = parseInt(hexColor.slice(3, 5), 16);
    const b = parseInt(hexColor.slice(5, 7), 16);
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness > 128 ? '#000000' : '#ffffff';
  };

  const getLabelColor = (label) => {
    const colorMap = {
      promo: colorSettings.label_promo,
      recommended: colorSettings.label_recommended,
      new: colorSettings.label_new,
    };
    return colorMap[label] || '#6b7280';
  };

  const getTranslatedText = (textObj) => {
    return textObj?.[language] || textObj?.en || '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${BACKEND_URL}/api/contact`, {
        ...formData,
        apartment_id: id,
        captcha_answer: parseInt(formData.captcha_answer),
      });
      toast.success('Message sent successfully!');
      setFormData({ name: '', email: '', subject: '', message: '', captcha_answer: '' });
    } catch (error) {
      toast.error('Failed to send message. Check captcha answer.');
    }
  };

  const handleShare = () => {
    const url = window.location.href;
    navigator.clipboard.writeText(url);
    toast.success('Link copied to clipboard!');
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-32 flex items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  if (!apartment) {
    return (
      <div className="min-h-screen pt-32 flex items-center justify-center">
        <p className="text-muted-foreground">Apartment not found</p>
      </div>
    );
  }

  return (
    <div data-testid="apartment-detail-page" className="min-h-screen pt-32 pb-24">
      <div className="container mx-auto px-6">
        {/* Back Button */}
        <Link
          to="/apartments"
          data-testid="back-to-apartments"
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors mb-8"
        >
          <ArrowLeft size={20} />
          {t('apartments')}
        </Link>

        {/* Gallery */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <div className="relative bg-slate-900/50 mb-4 flex items-center justify-center" style={{ minHeight: '400px', maxHeight: '70vh' }}>
            <img
              src={getImageUrl(apartment.images[currentImage]) || 'https://via.placeholder.com/1200x600'}
              alt={getTranslatedText(apartment.title)}
              className="max-w-full max-h-[70vh] w-auto h-auto object-contain"
            />
            <div className="absolute top-4 right-4 flex gap-2">
              <button
                data-testid="share-button"
                onClick={handleShare}
                className="bg-slate-950/70 backdrop-blur-xl border border-white/10 p-3 text-foreground hover:text-primary transition-colors"
              >
                <Share2 size={20} />
              </button>
            </div>
            {/* Navigation arrows for multiple images */}
            {apartment.images.length > 1 && (
              <>
                <button
                  onClick={() => setCurrentImage(prev => prev === 0 ? apartment.images.length - 1 : prev - 1)}
                  className="absolute left-4 top-1/2 -translate-y-1/2 bg-slate-950/70 backdrop-blur-xl border border-white/10 p-3 text-foreground hover:text-primary transition-colors"
                >
                  <ArrowLeft size={20} />
                </button>
                <button
                  onClick={() => setCurrentImage(prev => prev === apartment.images.length - 1 ? 0 : prev + 1)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 bg-slate-950/70 backdrop-blur-xl border border-white/10 p-3 text-foreground hover:text-primary transition-colors rotate-180"
                >
                  <ArrowLeft size={20} />
                </button>
              </>
            )}
          </div>
          {apartment.images.length > 1 && (
            <div className="flex flex-wrap gap-3 justify-center">
              {apartment.images.map((img, index) => (
                <button
                  key={index}
                  data-testid={`thumbnail-${index}`}
                  onClick={() => setCurrentImage(index)}
                  className={`w-24 h-24 border-2 transition-colors overflow-hidden flex-shrink-0 ${
                    currentImage === index ? 'border-primary' : 'border-transparent hover:border-white/20'
                  }`}
                >
                  <img src={getImageUrl(img)} alt="" className="w-full h-full object-contain bg-slate-900/30" />
                </button>
              ))}
            </div>
          )}
        </motion.div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Main Content */}
          <div className="lg:col-span-2 order-1">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h1 data-testid="apartment-title" className="text-4xl md:text-5xl font-serif mb-4">
                    {getTranslatedText(apartment.title)}
                  </h1>
                  <div className="flex flex-wrap items-center gap-2">
                    <span
                      className="px-3 py-1 text-xs uppercase tracking-wider font-bold rounded"
                      style={{
                        backgroundColor: apartment.status === 'available' 
                          ? colorSettings.status_available 
                          : apartment.status === 'available_soon'
                          ? colorSettings.status_available_soon
                          : colorSettings.status_occupied,
                        color: '#ffffff'
                      }}
                    >
                      {apartment.status === 'available' 
                        ? t('available') 
                        : apartment.status === 'available_soon'
                        ? (language === 'ro' ? `Disponibil în ${apartment.available_in_months || 1} ${apartment.available_in_months === 1 ? 'lună' : 'luni'}` : `Available in ${apartment.available_in_months || 1} months`)
                        : t('occupied')}
                    </span>
                    {apartment.labels?.map((label) => {
                      const bgColor = getLabelColor(label);
                      const textColor = getContrastColor(bgColor);
                      return (
                        <span
                          key={label}
                          className="px-3 py-1 text-xs uppercase tracking-wider font-bold rounded"
                          style={{ backgroundColor: bgColor, color: textColor }}
                        >
                          {label}
                        </span>
                      );
                    })}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-serif text-primary">
                    {convertPrice(apartment.price_eur, apartment.price_ron)}
                  </div>
                  <div className="text-sm text-muted-foreground">/ month</div>
                </div>
              </div>

              <div className="prose prose-invert max-w-none mb-12">
                <p className="text-lg leading-relaxed text-muted-foreground whitespace-pre-line">
                  {getTranslatedText(apartment.description)}
                </p>
              </div>

              {/* Property Type & Area */}
              {(apartment.apartment_type || apartment.area) && (
                <div className="mb-12 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {apartment.apartment_type && (
                    <div className="p-4 bg-slate-900/50 border border-white/5">
                      <h4 className="text-xs uppercase tracking-[0.2em] text-primary mb-2">
                        {language === 'ro' ? 'Tip Proprietate' : language === 'fr' ? 'Type de Propriété' : 'Property Type'}
                      </h4>
                      <p className="text-lg font-semibold">{apartment.apartment_type}</p>
                    </div>
                  )}
                  {apartment.area && (
                    <div className="p-4 bg-slate-900/50 border border-white/5">
                      <h4 className="text-xs uppercase tracking-[0.2em] text-primary mb-2">
                        {language === 'ro' ? 'Zonă' : language === 'fr' ? 'Zone' : 'Area'}
                      </h4>
                      <p className="text-lg font-semibold">{apartment.area}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Features */}
              {apartment.features && apartment.features.length > 0 && (
                <div className="mb-12">
                  <h3 className="text-xs uppercase tracking-[0.2em] font-bold text-primary mb-4">{t('features')}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {apartment.features.map((feature, index) => (
                      <div
                        key={index}
                        data-testid={`feature-${index}`}
                        className="flex items-center gap-3 p-4 bg-slate-900/50 border border-white/5"
                      >
                        <Check size={16} className="text-primary" />
                        <span className="text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* External Link */}
              {apartment.external_link && (
                <a
                  href={apartment.external_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="external-link"
                  className="inline-flex items-center gap-2 border border-primary/50 text-primary hover:bg-primary/10 px-6 py-3 transition-all"
                >
                  {language === 'ro'
                    ? 'Vezi pe Airbnb/Booking'
                    : language === 'fr'
                      ? 'Voir sur Airbnb/Booking'
                      : 'View on Airbnb/Booking'}
                  <ExternalLink size={16} />
                </a>
              )}
          </motion.div>
          </div>

          {/* Map - Shows before Contact on mobile (order-2), after on desktop */}
          {apartment.map_embed && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25 }}
              className="lg:col-span-2 order-2 lg:order-4"
            >
              <h3 className="text-xs uppercase tracking-[0.2em] font-bold text-primary mb-4">
                {language === 'ro' ? 'Locație' : language === 'fr' ? 'Emplacement' : 'Location'}
              </h3>
              <div className="h-72 md:h-96" dangerouslySetInnerHTML={{ __html: apartment.map_embed }} />
              {apartment.map_address && (
                <p className="text-sm text-muted-foreground mt-2">📍 {apartment.map_address}</p>
              )}
            </motion.div>
          )}

          {/* Sidebar - Contact Form - order-3 on mobile, order-2 on desktop */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="glass p-8 order-3 lg:order-2 lg:row-span-2"
          >
            <h3 className="text-2xl font-serif mb-6">{t('contact_us')}</h3>
            <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-6">
              <div>
                <input
                  type="text"
                  data-testid="contact-name"
                  placeholder={t('name')}
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="w-full bg-transparent border-b border-white/20 py-3 text-sm focus:outline-none focus:border-primary transition-colors placeholder:text-white/30"
                />
              </div>
              <div>
                <input
                  type="email"
                  data-testid="contact-email"
                  placeholder={t('email')}
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="w-full bg-transparent border-b border-white/20 py-3 text-sm focus:outline-none focus:border-primary transition-colors placeholder:text-white/30"
                />
              </div>
              <div>
                <input
                  type="text"
                  data-testid="contact-subject"
                  placeholder={t('subject')}
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  required
                  className="w-full bg-transparent border-b border-white/20 py-3 text-sm focus:outline-none focus:border-primary transition-colors placeholder:text-white/30"
                />
              </div>
              <div>
                <textarea
                  data-testid="contact-message"
                  placeholder={t('message')}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  required
                  rows={4}
                  className="w-full bg-transparent border-b border-white/20 py-3 text-sm focus:outline-none focus:border-primary transition-colors placeholder:text-white/30 resize-none"
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-2 block">8 + 6 = ?</label>
                <input
                  type="number"
                  data-testid="contact-captcha"
                  placeholder="Answer"
                  value={formData.captcha_answer}
                  onChange={(e) => setFormData({ ...formData, captcha_answer: e.target.value })}
                  required
                  className="w-full bg-transparent border-b border-white/20 py-3 text-sm focus:outline-none focus:border-primary transition-colors placeholder:text-white/30"
                />
              </div>
              <button
                type="submit"
                data-testid="contact-submit"
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90 px-6 py-4 font-serif text-sm uppercase tracking-wider transition-all"
              >
                {t('send_message')}
              </button>
            </form>
          </motion.div>
        </div>

      </div>
    </div>
  );
};

export default ApartmentDetailPage;